import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Card, Badge, Input, Select, cn } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiChevronLeft, FiStar, FiPlus, FiEdit2, FiTrash2, FiExternalLink, FiCheck, FiX,
} = FiIcons;

// ── Constants ───────────────────────────────────────────────────────────────

const SOURCE_TYPE_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: 'Top Producer', value: 'top_producer' },
  { label: 'Agency Owner', value: 'agency_owner' },
  { label: 'Educator', value: 'educator' },
  { label: 'Vendor', value: 'vendor' },
  { label: 'Unknown', value: 'unknown' },
];

const CATEGORY_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: 'Recruiting', value: 'recruiting' },
  { label: 'Life Insurance', value: 'life_insurance' },
  { label: 'Both', value: 'both' },
];

const TRAFFIC_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: 'Organic', value: 'organic' },
  { label: 'Paid', value: 'paid' },
  { label: 'Both', value: 'both' },
];

const STATUS_OPTIONS = [
  { label: 'Researching', value: 'researching' },
  { label: 'Testing', value: 'testing' },
  { label: 'Active', value: 'active' },
  { label: 'Archived', value: 'archived' },
];

const PLATFORM_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: 'Facebook', value: 'facebook' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'YouTube', value: 'youtube' },
  { label: 'TikTok', value: 'tiktok' },
  { label: 'LinkedIn', value: 'linkedin' },
  { label: 'Other', value: 'other' },
];

const LINK_TYPE_OPTIONS = [
  { label: '— Select —', value: '' },
  { label: 'Ad', value: 'ad' },
  { label: 'Post', value: 'post' },
  { label: 'VSL', value: 'vsl' },
  { label: 'Funnel', value: 'funnel' },
  { label: 'Profile', value: 'profile' },
  { label: 'Video', value: 'video' },
  { label: 'Website', value: 'website' },
  { label: 'Other', value: 'other' },
];

const PLATFORM_BADGE_COLORS = {
  facebook: 'bg-blue-100 text-blue-800',
  instagram: 'bg-pink-100 text-pink-800',
  youtube: 'bg-red-100 text-red-800',
  tiktok: 'bg-gray-900 text-white',
  linkedin: 'bg-sky-100 text-sky-800',
  other: 'bg-gray-100 text-gray-700',
};

const LINK_TYPE_BADGE_COLORS = {
  ad: 'bg-purple-100 text-purple-800',
  post: 'bg-green-100 text-green-800',
  vsl: 'bg-orange-100 text-orange-800',
  funnel: 'bg-violet-100 text-violet-800',
  profile: 'bg-cyan-100 text-cyan-800',
  video: 'bg-rose-100 text-rose-800',
  website: 'bg-teal-100 text-teal-800',
  other: 'bg-gray-100 text-gray-700',
};

function formatLabel(value) {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StarRating({ value, onChange }) {
  return (
    <span className="inline-flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = value != null && n <= value;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n === value ? null : n)}
            className="p-0.5 rounded hover:scale-110 transition-transform"
            aria-label={`Set credibility to ${n} stars`}
          >
            <SafeIcon
              icon={FiStar}
              className={cn('w-5 h-5', filled ? 'text-amber-400 fill-amber-400' : 'text-gray-300 fill-gray-100')}
            />
          </button>
        );
      })}
    </span>
  );
}

function SectionLabel({ children }) {
  return (
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 pt-2 border-t border-gray-100 first:border-t-0 first:pt-0">
      {children}
    </h3>
  );
}

function Textarea({ label, rows = 3, value, onChange, placeholder, helper }) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      {helper && <p className="text-xs text-gray-400 mb-1">{helper}</p>}
      <textarea
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-y transition-shadow"
      />
    </div>
  );
}

// ── Empty form builders ──────────────────────────────────────────────────────

function buildEmptyForm() {
  return {
    title: '',
    author_name: '',
    source_type: '',
    description: '',
    category: '',
    traffic_type: '',
    status: 'researching',
    credibility: null,
    worth_testing: false,
    captured_at: '',
    notes: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    linkedin_url: '',
    youtube_url: '',
    website_url: '',
    hook: '',
    offer_notes: '',
    funnel_breakdown: '',
    screenshot_url: '',
  };
}

function buildFormFromResource(resource) {
  if (!resource) return buildEmptyForm();
  return {
    title: resource.title || '',
    author_name: resource.author_name || '',
    source_type: resource.source_type || '',
    description: resource.description || '',
    category: resource.category || '',
    traffic_type: resource.traffic_type || '',
    status: resource.status || 'researching',
    credibility: resource.credibility ?? null,
    worth_testing: resource.worth_testing ?? false,
    captured_at: resource.captured_at || '',
    notes: resource.notes || '',
    facebook_url: resource.facebook_url || '',
    instagram_url: resource.instagram_url || '',
    tiktok_url: resource.tiktok_url || '',
    linkedin_url: resource.linkedin_url || '',
    youtube_url: resource.youtube_url || '',
    website_url: resource.website_url || '',
    hook: resource.hook || '',
    offer_notes: resource.offer_notes || '',
    funnel_breakdown: resource.funnel_breakdown || '',
    screenshot_url: resource.screenshot_url || '',
  };
}

function buildPayload(form) {
  return {
    title: form.title.trim(),
    author_name: form.author_name.trim() || null,
    source_type: form.source_type || null,
    description: form.description.trim() || null,
    category: form.category || null,
    traffic_type: form.traffic_type || null,
    status: form.status,
    credibility: form.credibility ?? null,
    worth_testing: form.worth_testing,
    captured_at: form.captured_at || null,
    notes: form.notes.trim() || null,
    facebook_url: form.facebook_url.trim() || null,
    instagram_url: form.instagram_url.trim() || null,
    tiktok_url: form.tiktok_url.trim() || null,
    linkedin_url: form.linkedin_url.trim() || null,
    youtube_url: form.youtube_url.trim() || null,
    website_url: form.website_url.trim() || null,
    hook: form.hook.trim() || null,
    offer_notes: form.offer_notes.trim() || null,
    funnel_breakdown: form.funnel_breakdown.trim() || null,
    screenshot_url: form.screenshot_url.trim() || null,
  };
}

function buildEmptyLinkForm() {
  return { label: '', url: '', platform: '', link_type: '', notes: '' };
}

// ── Links Manager ────────────────────────────────────────────────────────────

function LinksManager({ resourceId, agencyId }) {
  const { addToast } = useToast();
  const [links, setLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editForm, setEditForm] = useState(buildEmptyLinkForm());
  const [addForm, setAddForm] = useState(buildEmptyLinkForm());
  const [addLabelError, setAddLabelError] = useState('');
  const [addUrlError, setAddUrlError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadLinks = useCallback(async () => {
    if (!resourceId) return;
    setLoadingLinks(true);
    try {
      const data = await api.marketingIntelligence.links.getByResource(resourceId);
      setLinks(data);
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to load links', message: err.message });
    } finally {
      setLoadingLinks(false);
    }
  }, [resourceId, addToast]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const handleAdd = async () => {
    let valid = true;
    if (!addForm.label.trim()) { setAddLabelError('Label is required'); valid = false; }
    else setAddLabelError('');
    if (!addForm.url.trim()) { setAddUrlError('URL is required'); valid = false; }
    else setAddUrlError('');
    if (!valid) return;

    setSaving(true);
    try {
      await api.marketingIntelligence.links.add({
        resource_id: resourceId,
        agency_id: agencyId,
        label: addForm.label.trim(),
        url: addForm.url.trim(),
        platform: addForm.platform || null,
        link_type: addForm.link_type || null,
        notes: addForm.notes.trim() || null,
      });
      addToast({ type: 'success', title: 'Link added' });
      setAddForm(buildEmptyLinkForm());
      await loadLinks();
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to add link', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (link) => {
    setEditingLinkId(link.id);
    setEditForm({
      label: link.label || '',
      url: link.url || '',
      platform: link.platform || '',
      link_type: link.link_type || '',
      notes: link.notes || '',
    });
  };

  const handleEditSave = async (linkId) => {
    setSaving(true);
    try {
      await api.marketingIntelligence.links.update(linkId, {
        label: editForm.label.trim(),
        url: editForm.url.trim(),
        platform: editForm.platform || null,
        link_type: editForm.link_type || null,
        notes: editForm.notes.trim() || null,
      });
      addToast({ type: 'success', title: 'Link updated' });
      setEditingLinkId(null);
      await loadLinks();
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to update link', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Delete link "${link.label}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await api.marketingIntelligence.links.delete(link.id);
      addToast({ type: 'success', title: 'Link deleted' });
      await loadLinks();
    } catch (err) {
      addToast({ type: 'error', title: 'Failed to delete link', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const setAdd = (field) => (e) => setAddForm((p) => ({ ...p, [field]: e.target.value }));
  const setEdit = (field) => (e) => setEditForm((p) => ({ ...p, [field]: e.target.value }));

  return (
    <div className="space-y-4">
      <SectionLabel>Ad &amp; Content Links</SectionLabel>

      {loadingLinks ? (
        <div className="flex items-center gap-2 py-3">
          <Spinner size="sm" className="text-primary" />
          <span className="text-sm text-gray-400">Loading links…</span>
        </div>
      ) : links.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">No links added yet.</p>
      ) : (
        <div className="space-y-2">
          {links.map((link) =>
            editingLinkId === link.id ? (
              <div key={link.id} className="border border-primary/30 rounded-lg p-3 bg-blue-50/40 space-y-2">
                <Input
                  label="Label *"
                  value={editForm.label}
                  onChange={setEdit('label')}
                  placeholder="e.g. Main VSL"
                />
                <Input
                  label="URL *"
                  value={editForm.url}
                  onChange={setEdit('url')}
                  placeholder="https://…"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Select
                    label="Platform"
                    value={editForm.platform}
                    onChange={setEdit('platform')}
                    options={PLATFORM_OPTIONS}
                  />
                  <Select
                    label="Link Type"
                    value={editForm.link_type}
                    onChange={setEdit('link_type')}
                    options={LINK_TYPE_OPTIONS}
                  />
                </div>
                <Input label="Notes" value={editForm.notes} onChange={setEdit('notes')} placeholder="Optional notes…" />
                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={() => handleEditSave(link.id)} isLoading={saving} disabled={saving}>
                    <SafeIcon icon={FiCheck} className="mr-1 w-3.5 h-3.5" />
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingLinkId(null)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div
                key={link.id}
                className="flex items-start justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2.5 bg-white hover:border-gray-300 transition-colors"
              >
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-medium text-gray-900">{link.label}</span>
                    {link.platform && (
                      <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold', PLATFORM_BADGE_COLORS[link.platform] || PLATFORM_BADGE_COLORS.other)}>
                        {formatLabel(link.platform)}
                      </span>
                    )}
                    {link.link_type && (
                      <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold', LINK_TYPE_BADGE_COLORS[link.link_type] || LINK_TYPE_BADGE_COLORS.other)}>
                        {formatLabel(link.link_type)}
                      </span>
                    )}
                  </div>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1 max-w-full"
                  >
                    <span className="truncate max-w-[260px]">{link.url}</span>
                    <SafeIcon icon={FiExternalLink} className="w-3 h-3 flex-shrink-0" />
                  </a>
                  {link.notes && (
                    <p className="text-xs text-gray-500 truncate">{link.notes}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => startEdit(link)}
                    className="p-1.5 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100"
                    title="Edit link"
                  >
                    <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(link)}
                    className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50"
                    title="Delete link"
                  >
                    <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Add link mini-form */}
      <div className="border border-dashed border-gray-300 rounded-lg p-3 space-y-2 bg-gray-50/60">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Add Link</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Input
            label="Label *"
            value={addForm.label}
            onChange={setAdd('label')}
            placeholder="e.g. January Recruiting Ad"
            error={addLabelError}
          />
          <Input
            label="URL *"
            value={addForm.url}
            onChange={setAdd('url')}
            placeholder="https://…"
            error={addUrlError}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Select
            label="Platform"
            value={addForm.platform}
            onChange={setAdd('platform')}
            options={PLATFORM_OPTIONS}
          />
          <Select
            label="Link Type"
            value={addForm.link_type}
            onChange={setAdd('link_type')}
            options={LINK_TYPE_OPTIONS}
          />
        </div>
        <Input
          label="Notes"
          value={addForm.notes}
          onChange={setAdd('notes')}
          placeholder="Optional notes…"
        />
        <Button size="sm" onClick={handleAdd} isLoading={saving} disabled={saving}>
          <SafeIcon icon={FiPlus} className="mr-1.5 w-3.5 h-3.5" />
          Add Link
        </Button>
      </div>
    </div>
  );
}

// ── Main Form Page ────────────────────────────────────────────────────────────

export default function MarketingIntelligenceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(buildEmptyForm());
  const [titleError, setTitleError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const set = (field) => (e) => {
    const value = e.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    setLoading(true);
    api.marketingIntelligence.getById(id)
      .then((resource) => {
        if (!cancelled) {
          setForm(buildFormFromResource(resource));
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          addToast({ type: 'error', title: 'Failed to load resource', message: err.message });
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [id, isEdit, addToast]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setTitleError('Title is required');
      return;
    }
    setTitleError('');
    setSaving(true);
    try {
      const payload = buildPayload(form);
      if (isEdit) {
        await api.marketingIntelligence.update(id, payload);
        addToast({ type: 'success', title: 'Resource updated' });
      } else {
        const created = await api.marketingIntelligence.create({
          ...payload,
          agency_id: user.agency_id,
          created_by: user.id,
        });
        addToast({ type: 'success', title: 'Resource created' });
        navigate(`/marketing-intelligence/${created.id}/edit`, { replace: true });
      }
    } catch (err) {
      addToast({ type: 'error', title: isEdit ? 'Update failed' : 'Create failed', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-sm text-gray-500">Loading resource…</p>
      </div>
    );
  }

  return (
    <div className="min-h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/marketing-intelligence"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary font-medium transition-colors"
        >
          <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
          Back to Marketing Intelligence
        </Link>
        <Button onClick={handleSave} isLoading={saving} disabled={saving}>
          {isEdit ? 'Save Changes' : 'Save Resource'}
        </Button>
      </div>

      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">
          {isEdit ? 'Edit Resource' : 'Add New Resource'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isEdit
            ? 'Update the research profile and manage saved links below.'
            : 'Fill out what you know. After saving, you\'ll be taken to the edit page to add links.'}
        </p>
      </div>

      <form onSubmit={handleSave} noValidate>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Left column: Identity & Context ─────────────────────────── */}
          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Identity &amp; Context</h2>

            <Input
              label="Title *"
              value={form.title}
              onChange={set('title')}
              placeholder="Resource name"
              error={titleError}
            />

            <Input
              label="Author / Source Name"
              value={form.author_name}
              onChange={set('author_name')}
              placeholder="Person or brand being researched"
            />

            <Select
              label="Source Type"
              value={form.source_type}
              onChange={set('source_type')}
              options={SOURCE_TYPE_OPTIONS}
              helper="Who is this person?"
            />

            <Textarea
              label="Description"
              rows={3}
              value={form.description}
              onChange={set('description')}
              placeholder="What this resource or person is about…"
            />

            <Select
              label="Category"
              value={form.category}
              onChange={set('category')}
              options={CATEGORY_OPTIONS}
            />

            <Select
              label="Traffic Type"
              value={form.traffic_type}
              onChange={set('traffic_type')}
              options={TRAFFIC_OPTIONS}
            />

            <Select
              label="Status"
              value={form.status}
              onChange={set('status')}
              options={STATUS_OPTIONS}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Credibility</label>
              <StarRating
                value={form.credibility}
                onChange={(n) => setForm((prev) => ({ ...prev, credibility: n }))}
              />
            </div>

            <div className="flex items-start gap-3 py-1">
              <input
                id="worth_testing"
                type="checkbox"
                checked={form.worth_testing}
                onChange={(e) => setForm((prev) => ({ ...prev, worth_testing: e.target.checked }))}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label htmlFor="worth_testing" className="text-sm font-medium text-gray-700 cursor-pointer">
                Worth testing <span className="text-gray-400 font-normal">(not just observing)</span>
              </label>
            </div>

            <Input
              label="Captured Date"
              type="date"
              value={form.captured_at}
              onChange={set('captured_at')}
            />

            <Textarea
              label="Notes"
              rows={5}
              value={form.notes}
              onChange={set('notes')}
              placeholder="General research notes, swipe copy, observations…"
            />
          </Card>

          {/* ── Right column: Links & Funnel Intelligence ────────────────── */}
          <Card className="p-5 space-y-4">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Links &amp; Funnel Intelligence</h2>

            <SectionLabel>Platform Profile URLs</SectionLabel>

            <Input
              label="Facebook URL"
              value={form.facebook_url}
              onChange={set('facebook_url')}
              placeholder="https://facebook.com/…"
            />
            <Input
              label="Instagram URL"
              value={form.instagram_url}
              onChange={set('instagram_url')}
              placeholder="https://instagram.com/…"
            />
            <Input
              label="TikTok URL"
              value={form.tiktok_url}
              onChange={set('tiktok_url')}
              placeholder="https://tiktok.com/@…"
            />
            <Input
              label="LinkedIn URL"
              value={form.linkedin_url}
              onChange={set('linkedin_url')}
              placeholder="https://linkedin.com/in/…"
            />
            <Input
              label="YouTube URL"
              value={form.youtube_url}
              onChange={set('youtube_url')}
              placeholder="https://youtube.com/…"
            />
            <Input
              label="Website URL"
              value={form.website_url}
              onChange={set('website_url')}
              placeholder="https://…"
            />

            <SectionLabel>Funnel Intelligence</SectionLabel>

            <Textarea
              label="Hook"
              rows={2}
              value={form.hook}
              onChange={set('hook')}
              placeholder="The angle or hook they lead with…"
            />

            <Textarea
              label="Offer / Lead Magnet Notes"
              rows={2}
              value={form.offer_notes}
              onChange={set('offer_notes')}
              placeholder="What free offer or lead magnet they use…"
            />

            <Textarea
              label="Funnel Breakdown"
              rows={4}
              value={form.funnel_breakdown}
              onChange={set('funnel_breakdown')}
              placeholder="Step by step: ad → landing page → VSL → opt-in → follow-up sequence…"
            />

            <Input
              label="Screenshot URL"
              value={form.screenshot_url}
              onChange={set('screenshot_url')}
              placeholder="https://…"
            />

            {/* Links Manager — only available after save (edit page) */}
            {isEdit ? (
              <LinksManager resourceId={id} agencyId={user?.agency_id} />
            ) : (
              <div className="border border-dashed border-gray-200 rounded-lg p-4 text-center">
                <p className="text-sm text-gray-400">
                  Save the resource first, then you&apos;ll be able to add ad &amp; content links here.
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* Bottom save bar */}
        <div className="mt-6 flex justify-end">
          <Button type="submit" isLoading={saving} disabled={saving}>
            {isEdit ? 'Save Changes' : 'Save Resource'}
          </Button>
        </div>
      </form>
    </div>
  );
}
